import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Flex, Input, Modal, Typography, message } from '@/shared/antd-imports';
import { CheckOutlined, CloseOutlined, DownloadOutlined, LinkOutlined } from '@ant-design/icons';
import { useDecideDocumentMutation } from '@/store/api';
import type { ClientProjectDocument } from '@/types/project-documents';
import { documentFileLabel } from './document-utils';

interface DocumentDecisionModalProps {
  projectId: string;
  document: ClientProjectDocument;
  onOpenDocument: () => void;
  onClose: () => void;
}

const MAX_COMMENT = 2000;

/** Error code from the API answer (409 not_awaiting, 403 sign_in_required…). */
const errorCodeOf = (error: unknown): string | null => {
  const data = (error as { data?: { body?: { code?: unknown } } } | null)?.data;
  const code = data?.body?.code;
  return typeof code === 'string' ? code : null;
};

/**
 * DOC-3 portal half: the client reviews the version the provider sent for approval and approves or rejects it.
 * A reason is required to reject. The provider sees the client's name, the time and the IP address; an approval
 * locks the version.
 */
export const DocumentDecisionModal: React.FC<DocumentDecisionModalProps> = ({ projectId, document, onOpenDocument, onClose }) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [decide, { isLoading }] = useDecideDocumentMutation();
  const [pending, setPending] = useState<'approve' | 'reject' | null>(null);

  const submit = async (decision: 'approve' | 'reject') => {
    if (!document.version_id) return;
    if (decision === 'reject' && !comment.trim()) {
      setRejecting(true);
      return;
    }
    setPending(decision);
    try {
      const result = await decide({
        projectId,
        documentId: document.id,
        versionId: document.version_id,
        decision,
        comment: comment.trim() || undefined,
      }).unwrap();
      if (result && result.done === false) throw new Error(result.message);
      message.success(
        decision === 'approve'
          ? t('documents.approvedToast', { defaultValue: 'Thank you. The document is approved.' })
          : t('documents.rejectedToast', { defaultValue: 'Thank you. Your provider will see your reason.' })
      );
      onClose();
    } catch (error) {
      const code = errorCodeOf(error);
      message.error(
        code === 'not_awaiting' || code === 'not_current'
          ? t('documents.decisionStale', { defaultValue: 'This version no longer waits for your approval. Reload the page.' })
          : code === 'sign_in_required'
            ? t('documents.decisionSignIn', { defaultValue: 'Sign in with your own account to approve documents.' })
            : t('documents.decisionError', { defaultValue: 'Your answer could not be saved. Please try again.' })
      );
    } finally {
      setPending(null);
    }
  };

  const rejectMissingReason = rejecting && !comment.trim();

  return (
    <Modal
      open
      title={t('documents.reviewTitle', { defaultValue: 'Review: {{title}}', title: document.title })}
      onCancel={onClose}
      destroyOnClose
      footer={
        <Flex gap={8} justify="flex-end" wrap="wrap">
          <Button onClick={onClose} disabled={isLoading}>
            {t('documents.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button danger icon={<CloseOutlined />} loading={pending === 'reject'} disabled={isLoading && pending !== 'reject'} onClick={() => void submit('reject')}>
            {t('documents.reject', { defaultValue: 'Reject' })}
          </Button>
          <Button type="primary" icon={<CheckOutlined />} loading={pending === 'approve'} disabled={isLoading && pending !== 'approve'} onClick={() => void submit('approve')}>
            {t('documents.approve', { defaultValue: 'Approve' })}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={12}>
        <Typography.Text>
          {t('documents.reviewVersion', {
            defaultValue: 'Version {{no}} · {{file}}',
            no: document.version_no ?? 1,
            file: documentFileLabel(t, document),
          })}
        </Typography.Text>
        <Button
          icon={document.is_link ? <LinkOutlined /> : <DownloadOutlined />}
          onClick={onOpenDocument}
          disabled={!document.available}
          style={{ alignSelf: 'flex-start' }}
        >
          {document.is_link
            ? t('documents.openToReview', { defaultValue: 'Open the document' })
            : t('documents.downloadToReview', { defaultValue: 'Download the document' })}
        </Button>
        <Alert
          type="info"
          showIcon
          message={t('documents.decisionRecord', {
            defaultValue: 'Your name, the date and time, and your IP address are recorded with your answer. Approving locks this version.',
          })}
        />
        <div>
          <Typography.Text strong>
            <label htmlFor="document-decision-comment">
              {t('documents.comment', { defaultValue: 'Comment (required to reject)' })}
            </label>
          </Typography.Text>
          <Input.TextArea
            id="document-decision-comment"
            value={comment}
            onChange={event => setComment(event.target.value)}
            maxLength={MAX_COMMENT}
            showCount
            autoSize={{ minRows: 3, maxRows: 8 }}
            status={rejectMissingReason ? 'error' : undefined}
            aria-invalid={rejectMissingReason || undefined}
            aria-describedby={rejectMissingReason ? 'document-decision-comment-error' : undefined}
            placeholder={t('documents.commentPlaceholder', { defaultValue: 'What should change? (optional when approving)' })}
            style={{ marginTop: 4 }}
          />
          {rejectMissingReason && (
            <Typography.Text type="danger" id="document-decision-comment-error" role="alert" style={{ fontSize: 13 }}>
              {t('documents.reasonRequired', { defaultValue: 'Please say why, so your provider can fix it.' })}
            </Typography.Text>
          )}
        </div>
      </Flex>
    </Modal>
  );
};

export default DocumentDecisionModal;
